<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Background position
      </h1>
      <p class="page-title__sub">
        Utility classes for background positions.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.bg-left</code>
              </td>
              <td>
                <code class="code__prop">background-position: left;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-bottom-left-top</code>
              </td>
              <td>
                <code class="code__prop">background-position: left top;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-left-bottom</code>
              </td>
              <td>
                <code class="code__prop">background-position: left bottom;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-right</code>
              </td>
              <td>
                <code class="code__prop">background-position: right;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-right-top</code>
              </td>
              <td>
                <code class="code__prop">background-position: right top;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-right-bottom</code>
              </td>
              <td>
                <code class="code__prop">background-position: right bottom;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-center</code>
              </td>
              <td>
                <code class="code__prop">background-position: center;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-center-top</code>
              </td>
              <td>
                <code class="code__prop">background-position: center top;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.bg-center-bottom</code>
              </td>
              <td>
                <code class="code__prop">background-position: center bottom;</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Left*
        </h2>
      </header>
      <p>
        To set the background position for an image to the left use
        <code>{.bg-left}</code>, to change the y position suffix {-y},
        for example <code>.bg-left-{y}</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="bg-left bg-no-repeat..."&gt;&lt;/div&gt;
&lt;div class="bg-left-top bg-no-repeat..."&gt;&lt;/div&gt;
&lt;div class="bg-left-bottom bg-no-repeat..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-left</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-left bg-no-repeat" />
              </div>
            </div>
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-left-top</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-left-top bg-no-repeat" />
              </div>
            </div>
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-left-bottom</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-left-bottom bg-no-repeat" />
              </div>
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Right*
        </h2>
      </header>
      <p>
        To set the background position for an image to the right use
        <code>{.bg-right}</code>, to change the y position suffix {-y},
        for example <code>.bg-right-{y}</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="bg-right bg-no-repeat..."&gt;&lt;/div&gt;
&lt;div class="bg-right-top bg-no-repeat..."&gt;&lt;/div&gt;
&lt;div class="bg-right-bottom bg-no-repeat..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-right</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-right bg-no-repeat" />
              </div>
            </div>
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-right-top</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-right-top bg-no-repeat" />
              </div>
            </div>
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-right-bottom</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-right-bottom bg-no-repeat" />
              </div>
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Center*
        </h2>
      </header>
      <p>
        To set the background position for an image to the center use
        <code>{.bg-center}</code>, to change the y position suffix {-y},
        for example <code>.bg-left-{y}</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="bg-center bg-no-repeat..."&gt;&lt;/div&gt;
&lt;div class="bg-center-top bg-no-repeat..."&gt;&lt;/div&gt;
&lt;div class="bg-center-bottom bg-no-repeat..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-center</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-center bg-no-repeat" />
              </div>
            </div>
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-center-top</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image-small bg-center-top bg-no-repeat" />
              </div>
            </div>
            <div class="preview__block">
              <code class="code inline-flex margin-bottom-5">.bg-center-bottom</code>
              <div class="preview__image preview__image--border">
                <span class="preview__image -small bg-center-bottom bg-no-repeat" />
              </div>
            </div>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'BgPosition',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>
